import isMobile from "@web-monorepo/utilities/src/mobile";
import { clsx } from "clsx";
import FocusTrap from "focus-trap-react";
import React from "react";
import styles from "./PresentationPage.module.css";

/**
 * Identifiers for each toolkit pod's cast/display brand color. These are
 * stylistic labels, not status indicators — `groups` is red because the groups
 * tool's brand is red, not because something is dangerous.
 *
 * Pods that share a color (list/random, pairshare/timer) still get their own
 * variant so future recoloring of one doesn't affect the other.
 */
export type PresentationPageVariant =
  | "ideasQuestion"
  | "ideasVideo"
  | "groups"
  | "list"
  | "random"
  | "pairshare"
  | "timer"
  | "noise"
  | "radio"
  | "today";

interface PresentationPageProps {
  variant: PresentationPageVariant;
  onRequestHide?: VoidFunction;
  closeButtonAriaLabel?: string;
  className?: string;
}

const PresentationPage = ({
  variant,
  onRequestHide,
  closeButtonAriaLabel,
  className,
  children,
}: React.PropsWithChildren<PresentationPageProps>) => {
  return (
    <FocusTrap
      active={!isMobile.any}
      focusTrapOptions={{
        fallbackFocus: () => document.querySelector("[data-presentation-page-container]") as HTMLElement,
        allowOutsideClick: true,
      }}
    >
      <div className={clsx(styles.page, styles[variant], className)} data-presentation-page-container tabIndex={-1}>
        {onRequestHide && (
          <button
            data-name="presentation_page:render:other_actions_close"
            type="button"
            onClick={onRequestHide}
            aria-label={closeButtonAriaLabel || "Close"}
            className={styles.closeButton}
          >
            <CloseIcon />
          </button>
        )}
        {children}
      </div>
    </FocusTrap>
  );
};

function CloseIcon() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="currentColor" className={styles.closeIcon}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 9.172l4.243-4.243a2 2 0 112.828 2.828L14.828 12l4.243 4.243a2 2 0 01-2.828 2.828L12 14.828l-4.243 4.243a2 2 0 01-2.828-2.828L9.172 12 4.929 7.757A2 2 0 117.757 4.93L12 9.172z"
      />
    </svg>
  );
}

export default PresentationPage;
